import { Pipe, PipeTransform } from '@angular/core';
import _ from 'lodash';

@Pipe({
    name: 'smDate',
    standalone: false
})
export class SmDatePipe implements PipeTransform {
    private formatters = new Map<string, Intl.DateTimeFormat>();

    private getFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
        const locale = navigator.language;
        const key = JSON.stringify(options) + locale;
        if (!this.formatters.has(key)) {
            this.formatters.set(key, new Intl.DateTimeFormat(locale, options));
        }
        return this.formatters.get(key);
    }

    // Short:        1/7/2023
    // ShortGrid:    01/07/2023
    // Medium:       Jan 7, 2023
    // Long:         January 7, 2023
    // Full:         Saturday, January 7, 2023
    // Timestamp
    transform(date: any, format: SmDateFormats | string) : string {

        if (_.isNil(date) || date == "")
            return null;

        if (_.isString(date) || _.isNumber(date))
            date = new Date(date);

        switch (format) {
            case SmDateFormats.Short:
            case SmDateFormats[SmDateFormats.Short]:
                return this.getFormatter({ month: "numeric", day: "numeric", year: "numeric" }).format(date);

            case SmDateFormats.ShortGrid:
            case SmDateFormats[SmDateFormats.ShortGrid]:
                return this.getFormatter({ month: "2-digit", day: "2-digit", year: "numeric" }).format(date);

            case SmDateFormats.Medium:
            case SmDateFormats[SmDateFormats.Medium]:
                return this.getFormatter({ dateStyle: "medium" }).format(date);

            case SmDateFormats.Long:
            case SmDateFormats[SmDateFormats.Long]:
                return this.getFormatter({ dateStyle: "long" }).format(date);

            case SmDateFormats.Full:
            case SmDateFormats[SmDateFormats.Full]:
                return this.getFormatter({ dateStyle: "full" }).format(date);

            case SmDateFormats.Timestamp:
            case SmDateFormats[SmDateFormats.Timestamp]:
                return this.getFormatter({ dateStyle: "medium", timeStyle: "medium" }).format(date);

            case SmDateFormats.ShortTime:
            case SmDateFormats[SmDateFormats.ShortTime]:
                return this.getFormatter({ timeStyle: "short", hour12: true }).format(date);

            case SmDateFormats.ShortDayOfWeek:
            case SmDateFormats[SmDateFormats.ShortDayOfWeek]:
                return this.getFormatter({ weekday: 'short' }).format(date);

            case SmDateFormats.DayOfWeek:
            case SmDateFormats[SmDateFormats.DayOfWeek]:
                return this.getFormatter({ weekday: 'long' }).format(date);
        }
    }
}

export enum SmDateFormats {
    Short = "Short",
    ShortGrid = "ShortGrid",
    Medium = "Medium",
    Long = "Long",
    Full = "Full",
    Timestamp = "Timestamp",
    ShortTime = "ShortTime",
    ShortDayOfWeek = "ShortDayOfWeek",
    DayOfWeek = "DayOfWeek"
}

// Returns an Excel number format string that mirrors the locale ordering
// used by the pipe for a given format. Currently optimized for Short/ShortGrid.
export function getExcelDateFormat(format: SmDateFormats | string, locale?: string): string {
    const currentLocale = locale || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

    const buildPattern = (options: Intl.DateTimeFormatOptions, padded: boolean): string => {
        const formatter = new Intl.DateTimeFormat(currentLocale, options);
        // Use distinct placeholder values so we can safely replace
        const sample = new Date(2023, 10, 22); // Nov (11), 22, 2023
        const rendered = formatter.format(sample);
        // Replace year first to avoid partial overlaps
        let pattern = rendered.replace('2023', 'yyyy');
        // Replace month/day tokens. We expect the sample to include "11" and "22".
        // Some locales may include additional spaces or characters; we preserve them.
        const mToken = padded ? 'mm' : 'm';
        const dToken = padded ? 'dd' : 'd';
        pattern = pattern.replace('11', mToken);
        pattern = pattern.replace('22', dToken);
        return pattern;
    };

    switch (format) {
        case SmDateFormats.Short:
        case SmDateFormats[SmDateFormats.Short]:
            return buildPattern({ month: 'numeric', day: 'numeric', year: 'numeric' }, false);
        case SmDateFormats.ShortGrid:
        case SmDateFormats[SmDateFormats.ShortGrid]:
            return buildPattern({ month: '2-digit', day: '2-digit', year: 'numeric' }, true);
        default:
            // Fallback to a reasonable default using ShortGrid ordering
            return buildPattern({ month: '2-digit', day: '2-digit', year: 'numeric' }, true);
    }
}
